/**
 * File handling utility functions
 */

/**
 * Generates a random filename with given words and extension
 * @param {Array<string>} words - Array of words to choose from
 * @param {string} extension - File extension
 * @returns {string} Generated filename
 */
export function generateFileName(words, extension) {
    const randomWords = Array.from({ length: 3 }, () => 
        words[Math.floor(Math.random() * words.length)]
    );
    
    const timestamp = new Date().toISOString()
        .replace(/[^0-9]/g, '')
        .slice(0, 14);
    
    return `${randomWords.join('-')}-${timestamp}.${extension}`;
}

/**
 * Creates a download link for a blob
 * @param {Blob} blob - The blob to download
 * @param {string} filename - The filename
 */
export function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

/**
 * Loads an image from a URL
 * @param {string} url - The image URL
 * @returns {Promise<HTMLImageElement>} The loaded image
 */
export function loadImage(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = url;
    });
}

/**
 * Checks if a file is an image
 * @param {File} file - The file to check
 * @returns {boolean} True if the file is an image
 */
export function isImageFile(file) {
    return file && file.type.startsWith('image/');
}

/**
 * Checks if a file is a GIF
 * @param {File} file - The file to check
 * @returns {boolean} True if the file is a GIF
 */
export function isGifFile(file) {
    return file && file.type === 'image/gif';
}

/**
 * Gets the dimensions of an image
 * @param {HTMLImageElement|HTMLCanvasElement} image - The image
 * @returns {Object} Object containing width and height
 */
export function getImageDimensions(image) {
    return {
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height
    };
}

/**
 * Calculates dimensions that maintain aspect ratio within constraints
 * @param {number} containerWidth - Container width
 * @param {number} containerHeight - Container height
 * @param {number} imageAspectRatio - Image aspect ratio
 * @returns {Object} Object containing calculated width and height
 */
export function calculateFitDimensions(containerWidth, containerHeight, imageAspectRatio) {
    let width, height;
    const containerAspectRatio = containerWidth / containerHeight;

    if (imageAspectRatio > containerAspectRatio) {
        width = containerWidth;
        height = containerWidth / imageAspectRatio;
    } else {
        height = containerHeight;
        width = containerHeight * imageAspectRatio;
    }

    // Ensure dimensions are even numbers
    width = Math.floor(width / 2) * 2;
    height = Math.floor(height / 2) * 2;

    return { width, height };
}

/**
 * Creates a canvas with the specified dimensions
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @returns {HTMLCanvasElement} The created canvas
 */
export function createCanvas(width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
}

/**
 * Copies an image to a canvas with proper scaling
 * @param {HTMLCanvasElement} canvas - The target canvas
 * @param {HTMLImageElement|HTMLCanvasElement} image - The source image
 */
export function copyImageToCanvas(canvas, image) {
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
} 